import { type PersistStorage, type StorageValue } from "zustand/middleware";

/**
 * A session's token pair plus the stamp that ORDERS two views of it.
 *
 * `rotatedAt` is epoch milliseconds taken at every set — login, refresh,
 * sibling adoption, sign-out — and it is the only fact that can decide which
 * of two contexts holds the current pair. Comparing the tokens themselves
 * cannot: two contexts disagreeing about a rotated session hold two different
 * strings and neither string says which came second.
 */
export interface StampedTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number | null;
  rotatedAt: number;
}

/** The in-memory facts a sibling write is judged against. */
export interface AuthTokenSnapshot {
  refreshToken: string | null;
  rotatedAt: number;
}

/** A bag written before stamping existed carries no order at all, so it loses
 *  to every stamped write. Pre-production: this is the whole migration. */
const UNSTAMPED = 0;

let lastStamp = UNSTAMPED;

/**
 * The next rotation stamp for THIS JS context, strictly greater than the last
 * one it issued. `Date.now()` alone ties when two sets land in one millisecond
 * (login immediately followed by a mount refresh) and a tie is indistinguishable
 * from a regression to every reader; the carry keeps each context's own writes
 * totally ordered. Across contexts the wall clock orders them, which is sound
 * because the refresh lock makes two tabs rotating in the same millisecond
 * impossible.
 */
export function nextTokenStamp(): number {
  const now = Date.now();
  lastStamp = now > lastStamp ? now : lastStamp + 1;
  return lastStamp;
}

interface PersistedAuthBag {
  state?: { token?: unknown; refreshToken?: unknown; expiresAt?: unknown; rotatedAt?: unknown };
}

/**
 * Parses a persisted store bag into the stamped pair sibling contexts exchange.
 * Null for anything less than a live session — no bag, an unparseable bag, or
 * a bag whose tokens a sign-out cleared — so callers get ONE "no persisted
 * session" answer for every shape of absence.
 */
export function parsePersistedAuthTokens(raw: string | null): StampedTokens | null {
  if (raw === null) return null;
  let bag: PersistedAuthBag;
  try {
    bag = JSON.parse(raw) as PersistedAuthBag;
  } catch {
    return null;
  }
  const state = bag.state;
  if (
    state === undefined ||
    state === null ||
    typeof state.token !== "string" ||
    typeof state.refreshToken !== "string"
  ) {
    return null;
  }
  return {
    accessToken: state.token,
    refreshToken: state.refreshToken,
    expiresAt: typeof state.expiresAt === "number" ? state.expiresAt : null,
    rotatedAt: typeof state.rotatedAt === "number" ? state.rotatedAt : UNSTAMPED,
  };
}

/** What a guarded write resolved to: the state to persist, and the pair the
 *  writer must adopt when its own was the stale one. */
export interface GuardedAuthWrite<S> {
  state: S;
  keptNewer: StampedTokens | null;
}

/**
 * THE regression guard, stated over data so it can be reasoned about and
 * tested without a DOM.
 *
 * A persist middleware republishes the WHOLE partialized slice on every state
 * write, so an unrelated `set()` in one tab rewrites the token fields from that
 * tab's in-memory pair. When a sibling rotated in between, that write regresses
 * storage to a spent pair — and the spent pair then looks CURRENT to every
 * reader, including the refresh manager's own adopt check, which is how a tab
 * comes to replay a token its sibling already spent.
 *
 * So an outgoing write never lowers the stamp: if what is already persisted is
 * newer, its token fields survive the write and the rest of the slice lands
 * normally. The writer is told which pair won so it can catch its own memory
 * up.
 */
export function resolveGuardedAuthWrite<S>(
  persistedRaw: string | null,
  outgoing: S,
): GuardedAuthWrite<S> {
  const persisted = parsePersistedAuthTokens(persistedRaw);
  if (persisted === null) return { state: outgoing, keptNewer: null };

  const outgoingStamp =
    typeof (outgoing as { rotatedAt?: unknown }).rotatedAt === "number"
      ? (outgoing as { rotatedAt: number }).rotatedAt
      : UNSTAMPED;
  if (persisted.rotatedAt <= outgoingStamp) return { state: outgoing, keptNewer: null };

  return {
    state: {
      ...outgoing,
      token: persisted.accessToken,
      refreshToken: persisted.refreshToken,
      expiresAt: persisted.expiresAt,
      rotatedAt: persisted.rotatedAt,
    },
    keptNewer: persisted,
  };
}

/** The key-value backend the guarded storage reads and writes — `localStorage`
 *  in a browser. Declared structurally rather than as the DOM `Storage` type:
 *  this package is shared with React Native and must not require the DOM lib. */
export interface AuthTokenBackend {
  getItem: (name: string) => string | null;
  setItem: (name: string, value: string) => void;
  removeItem: (name: string) => void;
}

/** A persist storage that refuses token regression, plus the hook a store uses
 *  to adopt a pair its own write lost to. */
export interface AuthGuardedStorage<S> extends PersistStorage<S> {
  /** Called with the pair that survived a write this context tried to regress.
   *  The store wires `adoptTokens` here so memory catches up with storage. */
  onKeptNewer: (listener: (tokens: StampedTokens) => void) => void;
}

/**
 * The browser persist storage every portal writes its session through. JSON in
 * and out like `createJSONStorage`, with `resolveGuardedAuthWrite` between the
 * caller and the write.
 *
 * `backend` exists so the guard is testable without a DOM and so a host with no
 * `localStorage` (SSR, a worker) degrades to a no-op rather than throwing at
 * import time.
 */
export function createAuthGuardedStorage<S>(backend?: AuthTokenBackend): AuthGuardedStorage<S> {
  const listeners: ((tokens: StampedTokens) => void)[] = [];
  const store = (): AuthTokenBackend | null => {
    if (backend !== undefined) return backend;
    return (globalThis as { localStorage?: AuthTokenBackend }).localStorage ?? null;
  };

  return {
    getItem: (name) => {
      const raw = store()?.getItem(name) ?? null;
      if (raw === null) return null;
      try {
        return JSON.parse(raw) as StorageValue<S>;
      } catch {
        return null;
      }
    },
    setItem: (name, value) => {
      const backing = store();
      if (backing === null) return;
      const resolved = resolveGuardedAuthWrite(backing.getItem(name), value.state);
      backing.setItem(name, JSON.stringify({ ...value, state: resolved.state }));
      if (resolved.keptNewer === null) return;
      // Deferred one turn: this runs inside the persist subscriber, and a
      // listener that adopts calls `set()` again. Re-entering the middleware
      // mid-write is the kind of thing that works until it doesn't; the
      // adoption is a memory correction, not a write, so it can wait a tick.
      const won = resolved.keptNewer;
      void Promise.resolve().then(() => {
        for (const listener of listeners) listener(won);
      });
    },
    removeItem: (name) => {
      store()?.removeItem(name);
    },
    onKeptNewer: (listener) => {
      listeners.push(listener);
    },
  };
}

/** What a tab should do about a sibling's write to the shared session bag. */
export type SiblingAuthDecision =
  | { action: "ignore" }
  | { action: "clear" }
  | { action: "adopt"; tokens: StampedTokens };

/**
 * The `storage`-event decision, shared by all four portals so one rule governs
 * every tab. `storage` fires only in tabs that did NOT write, so an event on
 * the session key is exactly "a sibling changed this session".
 *
 * - No live session here: ignore. A sibling's sign-IN is never adopted into a
 *   tab parked on the login screen, which would mark it authenticated under a
 *   user object it never loaded.
 * - The persisted session is gone: clear. Each portal funnels that through its
 *   own teardown, which has more to settle than tokens.
 * - Otherwise adopt only a pair that is NEWER, so this tab's memory tracks
 *   rotations the moment they land instead of at its next refresh. Equal stamps
 *   with a different pair is the unstamped legacy bag, where "newer" cannot be
 *   asked and the sibling's write is the only answer available.
 */
export function resolveSiblingAuthWrite(
  raw: string | null,
  current: AuthTokenSnapshot,
): SiblingAuthDecision {
  if (current.refreshToken === null) return { action: "ignore" };
  const persisted = parsePersistedAuthTokens(raw);
  if (persisted === null) return { action: "clear" };
  if (persisted.rotatedAt > current.rotatedAt) return { action: "adopt", tokens: persisted };
  if (
    persisted.rotatedAt === current.rotatedAt &&
    persisted.refreshToken !== current.refreshToken
  ) {
    return { action: "adopt", tokens: persisted };
  }
  return { action: "ignore" };
}
